import { defineStore } from 'pinia'
import { ref } from 'vue'
import { notify } from '@kyvg/vue3-notification'
import { i18n } from '@/i18n/index.js'
import { useDigitalBankStore } from '@stores/digitalBank.js'
import { FolderPage } from '@/enums/folder-page.js'
import { useSharedFetcher } from '../services/sharedFetcher.js'
import {
  getFolders as getSharedFolders,
  createFolder as createSharedFolder,
  deleteFolder as deleteSharedFolder,
  addToFolder as addToSharedFolder,
  removeFromFolder as removeFromSharedFolder
} from '@services/folders'

export const useFolderStore = defineStore('folder', () => {
  const activeStep = ref(FolderPage.FolderList)
  const prevSteps = ref([])
  const nameFolder = ref('')
  const folders = ref([])
  const connections = ref([])
  const editFolder = ref(false)
  const digitalStore = useDigitalBankStore()
  const $t = i18n.global.t
  const fetcher = useSharedFetcher()

  const splitByType = (items) => ({
    connectionIds: items.filter((item) => item.type === 'user').map((item) => item.id),
    contactIds: items.filter((item) => item.type === 'contact').map((item) => item.id)
  })
  const getFolder = async (params) => {
    folders.value = await getSharedFolders(fetcher)
  }
  const createFolder = async () => {
    const data = await createSharedFolder(fetcher, nameFolder.value)

    if (data.id) {
      const response = await addToSharedFolder(fetcher, data.id, splitByType(connections.value))
      if (response.success) {
        notify({
          title: $t('congratulation'),
          text: $t('folder_success'),
          type: 'success',
          ignoreDuplicates: true
        })
      }
    }
  }
  const editExistingFolder = async (params) => {
    await addToSharedFolder(fetcher, params.id, splitByType(connections.value))
  }
  const deleteFolder = async (params) => {
    if (!params.id) return false

    await deleteSharedFolder(fetcher, params.id)

    folders.value = folders.value.filter((el) => el.id !== params.id)
  }

  // `params.id` is the row's *display* id (the target user's id for a
  // `type: 'user'` row, matching what's rendered in `digitalStore
  // .folderContacts` - see ContactRow.vue's `viewInfo()`/template). That is
  // NOT what POST /connections/remove-from-folder expects for a connection:
  // it needs the `users-connections` row's own id, passed separately as
  // `params.membershipId` (ContactRow.vue's `folderMembershipId()`). Mixing
  // these up silently no-ops the API call (a `connectionIds` id nothing
  // matches just removes nothing - `success: true` regardless).
  const deleteContactFromFolder = async (params) => {
    // Guards the id that is actually *sent* (`membershipId`), not the
    // display-only `params.id`: without a membershipId the request below
    // would post an empty/undefined id and no-op with `success: true`,
    // while the local `folderContacts` list would still drop the row - the
    // folder would look emptied until the next refetch.
    if (!params.membershipId) return false
    const data = await removeFromSharedFolder(
      fetcher,
      params.folderId,
      splitByType([{ id: params.membershipId, type: params.type }])
    )
    if (data.success) {
      // Matched on the { id, type } pair, never on `id` alone: a
      // `users-connections.id` and a `contacts.id` are independent
      // auto-increment sequences, so an id-only filter can drop an unrelated
      // contact row that happens to share the removed connection's number.
      digitalStore.folderContacts = digitalStore.folderContacts.filter(
        (el) => !(el.id === params.id && el.type === params.type)
      )
      if (!digitalStore.folderContacts.length) {
        await deleteFolder({ id: params.folderId })
        activeStep.value = 1
      }
    }
  }

  const moveTo = (page) => {
    prevSteps.value.push(activeStep.value)
    activeStep.value = page
  }

  const moveBack = () => {
    if (prevSteps.value) {
      activeStep.value = prevSteps.value.splice(-1, 1)[0]
    }
  }

  const clearFolderName = () => {
    nameFolder.value = ''
  }

  return {
    activeStep,
    moveTo,
    moveBack,
    nameFolder,
    getFolder,
    connections,
    createFolder,
    folders,
    deleteFolder,
    deleteContactFromFolder,
    editExistingFolder,
    editFolder,
    clearFolderName
  }
})
